import { styled } from '@/styles/stitches.config';
import { distance } from '@/utils/distance';
import { createEaseIn } from '@/utils/easing-functions';
import { useIsRightToLeft } from '@/utils/use-is-rtl';
import {
  animate,
  AnimationPlaybackControls,
  motion,
  MotionValue,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import {
  CARD_ASPECT_RATIO,
  CARD_FACTOR,
  CARD_MAX_WIDTH,
  DESKTOP_LAYOUT,
  GAP_FACTOR,
  LAYOUT_BREAKPOINT,
  MAX_GAP,
  MOBILE_LAYOUT,
} from './constants';
import { getCellInfoByIndex, getGridSizeByType } from './helpers';
import { useClickHandler } from './use-click-handler';
import { GridLayout } from './use-grid-layout';

type AnimatedCardProps = PropsWithChildren<{
  scrollX: MotionValue<number>;
  scrollY: MotionValue<number>;
  cardIndex: number;
  gridLayout: GridLayout;
  scrollBase: { x: number; y: number };
  selectedPosition: { x: number; y: number };
  handleClick: () => void;
  isIntroAnimation: boolean;
  isOutroAnimation: boolean;
  isSelected: boolean;
  hasLayoutInformation: boolean;
}>;

const getScaleAndOffset = (
  gridLayout: GridLayout,
  scrollX: number,
  scrollY: number,
  baseX: number,
  baseY: number
) => {
  const {
    cardWidth,
    cardHeight,
    gap,
    containerWidth,
    containerHeight,
    scaleFactor,
    radiusFactor,
    layout,
  } = gridLayout;

  // get polar coordinates
  const cx = baseX + scrollX + cardWidth * 0.5;
  const cy = baseY + scrollY + cardHeight * 0.5;
  const dx = cx - containerWidth * 0.5;
  const dy = cy - containerHeight * 0.5;
  const d = distance({ x: cx, y: cy }, { x: containerWidth * 0.5, y: containerHeight * 0.5 });
  const a = Math.atan2(dy, dx);

  const [columns] = getGridSizeByType(layout);

  // calculate scale based on polar coordinates
  const scaleInterpolator = easeInQuart;
  const scaleDivisor = (columns * (cardWidth + gap) - gap) * scaleFactor;

  const scale = scaleInterpolator(1 - d / scaleDivisor);

  // calculate x and y offset based on polar coordinates
  const radiusInterpolator = linear;
  const radiusDivisor = (columns * (cardWidth + gap) - gap) * radiusFactor;
  const radius = radiusInterpolator(1 - d / radiusDivisor);
  const x = Math.cos(a) * d * radius - dx;
  const y = Math.sin(a) * d * radius - dy;

  return { scale, x, y };
};

// easings
const easeInQuart = createEaseIn(4);
const linear = (value: number) => value;

export function AnimatedCard(props: AnimatedCardProps) {
  const {
    handleClick,
    scrollX,
    scrollY,
    cardIndex,
    gridLayout,
    scrollBase,
    selectedPosition,
    isIntroAnimation,
    isOutroAnimation,
    isSelected,
    hasLayoutInformation,
    children,
  } = props;

  const isRtl = useIsRightToLeft();
  const mod = useMemo(() => (isRtl ? -1 : 1), [isRtl]);

  const restSpeed = 0.001;
  const restDelta = 0.001;
  const baseStiffness = 269;
  const stiffnessRange = 6;
  const baseDamping = 31;

  const base = useRef({ x: 0, y: 0 });

  // we're adding this state to circumvent slow initial rendering
  // as a consequence of not completely being able to control
  // immediately setting spring props
  // after warmup, we toggle the motion values for the card element
  const [isWarmingUp, setWarmingUp] = useState(true);
  useEffect(() => {
    if (hasLayoutInformation) {
      const id = setTimeout(() => {
        setWarmingUp(false);
      }, 200);
      return () => {
        clearTimeout(id);
      };
    }
  }, [hasLayoutInformation]);

  const xMain = useSpring(0, {
    stiffness: baseStiffness + Math.random() * stiffnessRange,
    damping: baseDamping + Math.random() * 3,
    restSpeed,
    restDelta,
  });
  const yMain = useSpring(0, {
    stiffness: baseStiffness + Math.random() * stiffnessRange,
    damping: baseDamping + Math.random() * 3,
    restSpeed,
    restDelta,
  });
  const xOffset = useSpring(0, {
    stiffness: baseStiffness + Math.random() * stiffnessRange,
    damping: baseDamping + Math.random() * 7,
    restSpeed,
    restDelta,
  });
  const yOffset = useSpring(0, {
    stiffness: baseStiffness + Math.random() * stiffnessRange,
    damping: baseDamping + Math.random() * 7,
    restSpeed,
    restDelta,
  });
  const scale = useSpring(1, {
    stiffness: baseStiffness + Math.random() * stiffnessRange,
    damping: baseDamping + Math.random() * 3,
    restSpeed,
    restDelta,
  });
  const zIndex = useTransform(scale, (value: number) => Math.round(value * 100));
  const x = useTransform([xMain, xOffset], ([x1, x2]: number[]) => mod * (x1 + x2));
  const y = useTransform([yMain, yOffset], ([y1, y2]: number[]) => y1 + y2);

  // for the intro animation we're creating a separate set of values because we're not using spring animations here
  const xMainIntro = useMotionValue(0);
  const yMainIntro = useMotionValue(0);
  const xOffsetIntro = useMotionValue(0);
  const yOffsetIntro = useMotionValue(0);
  const scaleIntro = useMotionValue(1);
  const zIndexIntro = useTransform(scaleIntro, (value: number) => Math.round(value * 100));
  const xIntro = useTransform(
    [xMainIntro, xOffsetIntro] as MotionValue[],
    ([x1, x2]: number[]) => mod * (x1 + x2)
  );
  const yIntro = useTransform(
    [yMainIntro, yOffsetIntro] as MotionValue[],
    ([y1, y2]: number[]) => y1 + y2
  );

  const scaleOutro = useMotionValue(1);
  const xOutroBase = useMotionValue(0);
  const xOutro = useTransform(xOutroBase, (value: number) => mod * value);
  const yOutro = useMotionValue(0);
  const zIndexOutro = useMotionValue(0);

  // we're hiding and showing items after a certain scale threshold to prevent small cards on some viewports
  const opacity = useMotionValue(1);
  const prevScale = useRef(1);
  useEffect(() => {
    if (isIntroAnimation) {
      return;
    }
    const scaleThreshold = 0.14;
    const unsubscribe = scale.on('change', (value) => {
      if (
        (value >= scaleThreshold && prevScale.current < scaleThreshold) ||
        (value < scaleThreshold && prevScale.current >= scaleThreshold)
      ) {
        animate(opacity, value >= scaleThreshold ? 1 : 0, {
          type: 'tween',
          duration: value >= scaleThreshold ? 0.5 : 0.25,
          ease: value >= scaleThreshold ? 'easeOut' : 'easeIn',
        });
      }
      prevScale.current = value;
    });

    return () => {
      unsubscribe();
    };
  }, [opacity, scale, isIntroAnimation]);

  // set base x and y on mount and when resize occurs
  useEffect(() => {
    if (!hasLayoutInformation) {
      return;
    }

    const [colIndex, rowIndex] = getCellInfoByIndex(gridLayout.layout, cardIndex);
    const cardX = colIndex * (gridLayout.cardWidth + gridLayout.gap);
    const cardY = rowIndex * (gridLayout.cardHeight + gridLayout.gap);

    base.current.x = cardX;
    base.current.y = cardY;

    const {
      scale: s,
      x: offsetX,
      y: offsetY,
    } = getScaleAndOffset(gridLayout, scrollBase.x, scrollBase.y, base.current.x, base.current.y);

    // immediately setting springs using jump method
    // we do this to avoid springing into position on mount and on resize
    xMain.jump(scrollBase.x);
    yMain.jump(scrollBase.y);
    scale.jump(s);
    xOffset.jump(offsetX);
    yOffset.jump(offsetY);

    xMainIntro.set(scrollBase.x);
    yMainIntro.set(scrollBase.y);
    scaleIntro.set(s);
    xOffsetIntro.set(offsetX);
    yOffsetIntro.set(offsetY);

    scaleOutro.set(s);
    xOutroBase.set(scrollBase.x + offsetX);
    yOutro.set(scrollBase.y + offsetY);
  }, [
    base,
    cardIndex,
    gridLayout,
    xMain,
    yMain,
    scrollBase,
    scale,
    xOffset,
    yOffset,
    xMainIntro,
    yMainIntro,
    scaleIntro,
    xOffsetIntro,
    yOffsetIntro,
    scaleOutro,
    xOutro,
    yOutro,
    hasLayoutInformation,
    xOutroBase,
  ]);

  // this starts of the intro animation when coming from the introduction page
  useEffect(() => {
    if (!isIntroAnimation || !hasLayoutInformation) {
      return;
    }
    let scaleAnimation: AnimationPlaybackControls | undefined;
    let xOffsetAnimation: AnimationPlaybackControls | undefined;
    let yOffsetAnimation: AnimationPlaybackControls | undefined;

    // this timeout aligns with the timeout defined in <Overview/> for hiding the overlay
    const timeoutId = setTimeout(() => {
      const s = scaleIntro.get();
      const offsetX = xOffsetIntro.get();
      const offsetY = yOffsetIntro.get();

      // value of multiplier is chosen based on what works across various screensizes
      const multiplier = 4;
      scaleIntro.set(s * multiplier);
      xOffsetIntro.set(offsetX * multiplier);
      yOffsetIntro.set(offsetY * multiplier);

      scaleAnimation = animate(scaleIntro, s, {
        type: 'tween',
        delay: Math.random() * 0.15,
        duration: 0.9 + Math.random() * 0.2,
        ease: 'backOut',
      });
      xOffsetAnimation = animate(xOffsetIntro, offsetX, {
        type: 'tween',
        delay: Math.random() * 0.15,
        duration: 0.9 + Math.random() * 0.2,
        ease: 'backOut',
      });
      yOffsetAnimation = animate(yOffsetIntro, offsetY, {
        type: 'tween',
        delay: Math.random() * 0.15,
        duration: 0.9 + Math.random() * 0.2,
        ease: 'backOut',
      });
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      scaleAnimation && scaleAnimation.stop();
      xOffsetAnimation && xOffsetAnimation.stop();
      yOffsetAnimation && yOffsetAnimation.stop();
    };
  }, [isIntroAnimation, scaleIntro, xOffsetIntro, yOffsetIntro, hasLayoutInformation]);

  // outro animation handling
  useEffect(() => {
    if (isOutroAnimation) {
      if (isSelected) {
        const s =
          Math.max(
            gridLayout.containerHeight / gridLayout.cardHeight,
            gridLayout.containerWidth / gridLayout.cardWidth
          ) + 0.1;

        const scaleAnimation = animate(scaleOutro, s, {
          type: 'tween',
          delay: 0.25,
          duration: 1,
          ease: 'backIn',
        });

        const xAnimation = animate(xOutroBase, selectedPosition.x, {
          type: 'tween',
          delay: 0.5,
          duration: 0.75,
          ease: 'easeIn',
        });

        const yAnimation = animate(yOutro, selectedPosition.y, {
          type: 'tween',
          delay: 0.5,
          duration: 0.75,
          ease: 'easeIn',
        });

        zIndexOutro.set(1);

        return () => {
          scaleAnimation.stop();
          xAnimation.stop();
          yAnimation.stop();
        };
      } else {
        zIndexOutro.set(0);
      }
    }
  }, [
    isOutroAnimation,
    isSelected,
    scaleOutro,
    selectedPosition,
    scale,
    xOffset,
    yOffset,
    yOutro,
    zIndexOutro,
    gridLayout,
    xOutroBase,
  ]);

  // attach animation listeners
  useEffect(() => {
    if (isIntroAnimation || !hasLayoutInformation) {
      return;
    }
    const updateCard = () => {
      if (!gridLayout.containerWidth || !gridLayout.containerHeight) {
        return;
      }

      const {
        scale: s,
        x: offsetX,
        y: offsetY,
      } = getScaleAndOffset(
        gridLayout,
        scrollX.get(),
        scrollY.get(),
        base.current.x,
        base.current.y
      );

      xMain.set(scrollX.get());
      yMain.set(scrollY.get());
      xOffset.set(offsetX);
      yOffset.set(offsetY);
      scale.set(s);

      if (!isOutroAnimation) {
        scaleOutro.set(s);
        scaleOutro.set(s);
        xOutroBase.set(scrollX.get() + offsetX);
        yOutro.set(scrollY.get() + offsetY);
      }
    };

    const unsubscribeX = scrollX.on('change', updateCard);
    const unsubscribeY = scrollY.on('change', updateCard);

    return () => {
      unsubscribeX();
      unsubscribeY();
    };
  }, [
    base,
    scale,
    scaleOutro,
    gridLayout,
    scrollX,
    scrollY,
    xMain,
    yMain,
    xOffset,
    yOffset,
    yOutro,
    isIntroAnimation,
    isOutroAnimation,
    hasLayoutInformation,
    mod,
    xOutroBase,
  ]);

  const { onPointerDown, onPointerUp } = useClickHandler(handleClick, {
    pixelThreshold: 3,
    clickDuration: 300,
  });

  const [desktopColIndex, desktopRowIndex] = DESKTOP_LAYOUT[cardIndex];
  const [mobileColIndex, mobileRowIndex] = MOBILE_LAYOUT[cardIndex];

  return (
    <Root
      style={
        isWarmingUp || isIntroAnimation
          ? {
              x: xIntro,
              y: yIntro,
              scale: scaleIntro,
              zIndex: zIndexIntro,
              opacity,
            }
          : isOutroAnimation
          ? {
              x: xOutro,
              y: yOutro,
              scale: scaleOutro,
              zIndex: zIndexOutro,
              opacity,
            }
          : { x, y, scale, zIndex, opacity }
      }
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      css={{
        insetBlockStart: `calc(${mobileRowIndex} * (var(--card-height) + var(--gap)))`,
        insetInlineStart: `calc(${mobileColIndex} * (var(--card-width) + var(--gap)))`,

        [`@media screen and (min-width: ${LAYOUT_BREAKPOINT}px)`]: {
          insetBlockStart: `calc(${desktopRowIndex} * (var(--card-height) + var(--gap)))`,
          insetInlineStart: `calc(${desktopColIndex} * (var(--card-width) + var(--gap)))`,
        },
      }}
    >
      {children}
    </Root>
  );
}

const Root = styled(motion.div, {
  '--gap': `min(${MAX_GAP}px, ${GAP_FACTOR}vmax)`,
  '--card-width': `min(${CARD_MAX_WIDTH}px, ${CARD_FACTOR}vmax)`,
  '--card-height': `calc(var(--card-width) * ${CARD_ASPECT_RATIO})`,
  position: 'absolute',
  width: 'var(--card-width)',
  height: 'var(--card-height)',
  userSelect: 'none',
  touchAction: 'none',
  willChange: 'transform',
  fontSize: '1.25rem',
  '@bp5': {
    fontSize: '1.75rem',
  },
  '@bp10': {
    fontSize: '2rem',
  },
  '@media screen and (min-width: 90em)': {
    fontSize: '2.5rem',
  },
  '@media screen and (min-width: 110em)': {
    fontSize: '3rem',
  },
});
